import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  Button,
  IconButton,
  CircularProgress,
  Checkbox,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';

import {
  dateValueTypes,
  formatDate,
  formatDateAndTime,
} from '../../../utilities';
import * as projectActions from '../projectActions';

const formatValue = (key, value) => {
  if (_.includes(dateValueTypes, key)) {
    return formatDate(value);
  }

  return value;
};

const useStyles = makeStyles((theme) => ({
  detailsList: {
    listStyleType: 'none',
    margin: '12px 0 0',
    padding: 0,
  },
  infoIcon: {
    fontSize: '18px',
    verticalAlign: 'middle',
    color: '#666',
    cursor: 'pointer',
    position: 'relative',
    top: '-2px',
    marginLeft: '2px',
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '14px',
  },
  statusSummaryLink: {
    cursor: 'pointer',
    padding: 0,
    textTransform: 'none',
    textDecoration: 'underline',
    fontSize: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  statusTable: {
    tableLayout: 'fixed',
    width: '100%',
  },
  statusColumn: {
    width: '35%',
  },
  modifiedByColumn: {
    width: '35%',
  },
  modifiedDateColumn: {
    width: '30%',
  },
  tableBodyOverlay: {
    position: 'absolute',
    top: 70, // Approximate height of table header
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: theme.spacing(1),
    zIndex: 2,
    pointerEvents: 'none',
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& th': {
      color: '#ffffff',
      fontWeight: 'bold',
    },
    '& .MuiTableSortLabel-root': {
      color: '#ffffff',
      '&:hover': {
        color: '#ffffff',
      },
      '&.MuiTableSortLabel-active': {
        color: '#ffffff',
      },
    },
    '& .MuiTableSortLabel-icon': {
      color: '#ffffff !important',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  tableBodyDimmed: {
    '& tbody tr': {
      opacity: 0.5,
    },
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
}));

const ProjectDetailsListComponent = ({ 
  title, 
  details, 
  projectId, 
  statusCode,
  latestStatus,
  statusHistory,
  totalCount,
  statusLoading,
  statusError,
  fetchStatusHistory,
  resetStatusHistory,
}) => {
  const classes = useStyles();
  const [openStatusSummary, setOpenStatusSummary] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState('Modified_Timestamp');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  // Fetch status history when modal opens, page changes, rowsPerPage changes, or sort changes
  useEffect(() => {
    if (openStatusSummary && projectId) {
      fetchStatusHistory(
        projectId,
        rowsPerPage,
        page + 1, // API expects 1-based page number
        sortField,
        sortDirection
      );
    }
  }, [openStatusSummary, projectId, page, rowsPerPage, sortField, sortDirection, statusCode, fetchStatusHistory]);

  // Reset status history when modal closes or component unmounts
  useEffect(() => {
    if (!openStatusSummary) {
      resetStatusHistory();
    }
    
    // Cleanup on unmount
    return () => {
      resetStatusHistory();
    };
  }, [openStatusSummary, resetStatusHistory]);
  
  // Reset when project changes (not status, to avoid disruption during active viewing)
  useEffect(() => {
    resetStatusHistory();
  }, [projectId, resetStatusHistory]);

  const renderStatusTooltip = () => {    

    if (!latestStatus || statusLoading) {
      return (
        <div className={classes.tooltipContent}>
          <div>Modified by: N/A</div>
          <div>Modified date: N/A</div>
        </div>
      );
    }

    return (
      <div className={classes.tooltipContent}>
        <div>Modified by: {latestStatus.modified_by_user_name}</div>
        <div>Modified date: {formatDateAndTime(latestStatus.modified_timestamp)}</div>
      </div>
    );
  };

  const handleOpenStatusSummary = () => {
    setOpenStatusSummary(true);
    setPage(0);
    setSortField('Modified_Timestamp');
    setSortDirection('desc');
  };

  const handleCloseStatusSummary = () => {
    setOpenStatusSummary(false);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };
  
  // Server-side sorting handler
  const handleSort = (property) => {
    const isAsc = sortField === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(property);
    setPage(0); // Reset to first page when sorting changes
  };
  
  return (
    <>
      <ul className={classes.detailsList}>
      {title && (
      <li>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
      </li>
      )}
      {_.map(details, (detail, index) => (
        <React.Fragment key={`detail_${detail.label}`}>
          <li className="detailLi">
            <span className="detailLabel">
              {detail.label}:
            </span>
            &nbsp;{detail.key === 'discretionaryApproval' ? (
              <Checkbox
                checked={detail.value === 'Yes' || detail.value === true}
                disabled
                color="primary"
                size="small"
                style={{ padding: 0, marginLeft: '-2px', verticalAlign: 'middle' }}
              />
            ) : formatValue(detail.key, detail.value)}
            {detail.label === 'Status' && (
              <Tooltip title={renderStatusTooltip()} arrow placement="right">
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            )}
          </li>
          {detail.label === 'Status' && (
            <li className="detailLi">
              <Button
                onClick={handleOpenStatusSummary}
                className={classes.statusSummaryLink}
                color="primary"
              >
                Status Summary
              </Button>
            </li>
          )}
        </React.Fragment>
      ))}
    </ul>

      {/* Status Summary Dialog */}
      <Dialog
        open={openStatusSummary}
        onClose={handleCloseStatusSummary}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">Status Summary</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleCloseStatusSummary}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {statusError ? (
            <Typography className={classes.errorText}>
              Error loading status history: {statusError}
            </Typography>
          ) : statusLoading && statusHistory.length === 0 ? (
            // Initial load - show centered spinner
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            // Show table with overlay loader for pagination/sorting
            <>
              <div className={classes.tableContainer}>
                {statusLoading && (
                  <div className={classes.tableBodyOverlay}>
                    <CircularProgress size={24} />
                  </div>
                )}
                <Table className={`${classes.statusTable} ${statusLoading ? classes.tableBodyDimmed : ''}`}>
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                      <TableCell className={classes.statusColumn}>
                        <TableSortLabel
                          active={sortField === 'Status'}
                          direction={sortField === 'Status' ? sortDirection : 'asc'}
                          onClick={() => handleSort('Status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={classes.modifiedByColumn}>
                        <TableSortLabel
                          active={sortField === 'Modified_By'}
                          direction={sortField === 'Modified_By' ? sortDirection : 'asc'}
                          onClick={() => handleSort('Modified_By')}
                        >
                          Modified By
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={classes.modifiedDateColumn}>
                        <TableSortLabel
                          active={sortField === 'Modified_Timestamp'}
                          direction={sortField === 'Modified_Timestamp' ? sortDirection : 'asc'}
                          onClick={() => handleSort('Modified_Timestamp')}
                        >
                          Modified Date
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusHistory.length > 0 ? (
                      statusHistory.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className={classes.statusColumn}>{row.status_code_desc}</TableCell>
                          <TableCell className={classes.modifiedByColumn}>{row.modified_by_user_name}</TableCell>
                          <TableCell className={classes.modifiedDateColumn}>{formatDateAndTime(row.modified_timestamp)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No status history available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {totalCount > 0 && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onChangePage={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

ProjectDetailsListComponent.defaultProps = {
  details: [],
  title: '',
  projectId: null,
  statusCode: null,
  latestStatus: null,
  statusHistory: [],
  totalCount: 0,
  statusLoading: false,
  statusError: null,
};

ProjectDetailsListComponent.propTypes = {
  details: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.node,
  })),
  title: PropTypes.string,
  projectId: PropTypes.number,
  statusCode: PropTypes.number,
  latestStatus: PropTypes.shape({
    status_code_desc: PropTypes.string,
    modified_by_user_name: PropTypes.string,
    modified_timestamp: PropTypes.string,
  }),
  statusHistory: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    status_code_desc: PropTypes.string,
    modified_by_user_name: PropTypes.string,
    modified_timestamp: PropTypes.string,
  })),
  totalCount: PropTypes.number,
  statusLoading: PropTypes.bool,
  statusError: PropTypes.string,
  fetchStatusHistory: PropTypes.func.isRequired,
  resetStatusHistory: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  statusHistory: _.get(state, 'projectDetails.status.statusHistory.records', []),
  totalCount: _.get(state, 'projectDetails.status.statusHistory.totalCount', 0),
  statusLoading: _.get(state, 'projectDetails.status.loading', false),
  statusError: _.get(state, 'projectDetails.status.error', null),
});

const mapDispatchToProps = (dispatch) => ({
  fetchStatusHistory: (closing_id, limit, page, sortField, sortDirection) => 
    dispatch(projectActions.fetchStatusHistory(closing_id, limit, page, sortField, sortDirection)),
  resetStatusHistory: () => dispatch(projectActions.resetStatusHistory()),
});

export const ProjectDetailsList = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectDetailsListComponent);
